import { useCallback, useEffect, useLayoutEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { DesktopHeader } from '@/components/navigation/DesktopHeader'
import { Footer } from '@/components/navigation/Footer'
import { MobileBottomNavigation } from '@/components/navigation/MobileBottomNavigation'
import { MobileMenuOverlay } from '@/components/navigation/MobileMenuOverlay'
import {
  attachClickTracking,
  attachScrollTracking,
  attachTimeOnPageTracking,
  initAnalytics,
  trackPageView,
} from '@/analytics'

interface SiteLayoutProps {
  children: ReactNode
}

export function SiteLayout({ children }: SiteLayoutProps) {
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const openMobileMenu = useCallback(() => setIsMobileMenuOpen(true), [])
  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), [])

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname])

  useEffect(() => {
    initAnalytics()
    return attachClickTracking()
  }, [])

  useEffect(() => {
    const pagePath = `${location.pathname}${location.search}${location.hash}`
    trackPageView(pagePath, document.title)

    const detachScrollTracking = attachScrollTracking()
    const detachTimeTracking = attachTimeOnPageTracking()

    return () => {
      detachScrollTracking()
      detachTimeTracking()
    }
  }, [location.hash, location.pathname, location.search])

  return (
    <div className={`site-layout ${location.pathname === '/' ? 'site-layout--home' : ''}`}>
      <DesktopHeader onOpenMenu={openMobileMenu} />
      <main className="site-main">{children}</main>
      <Footer />
      <MobileBottomNavigation />
      <MobileMenuOverlay isOpen={isMobileMenuOpen} onClose={closeMobileMenu} />
    </div>
  )
}
